import React, { useState } from "react";

import { Device } from "@/features/devices/types";
import { useGetDeviceDetailQuery } from "@/features/devices/devicesApi";
import { useDeviceInfoOnDemandReport } from "@/features/devices/useDeviceInfoOnDemandReport";
import { decideDeviceInfoLiveRefreshMs } from "@/features/devices/deviceInfoLiveRefresh";
import { useDownloadSpeedTest } from "@/features/devices/useDownloadSpeedTest";
import {
  SPEED_TEST_DISCLAIMER,
  formatDownloadSpeedDisplay,
  formatUploadSpeedDisplay,
  lastTestedLabel,
  speedTestEndpointLabel,
  speedTestStatusLabel,
} from "@/features/devices/downloadSpeedTest";
import { isDeviceFleetCommandsEnabled } from "@/features/devices/devicePreview";
import {
  DEVICE_DETAIL_QUERY_OPTIONS,
  detailMatchesDevice,
  formatAppLockDisplay,
  formatDeviceDetailError,
  formatHealthTimestamp,
  formatPercentDisplay,
  formatReportedValue,
  formatSnapshotNote,
  formatWifiConnectionStatus,
  healthTimestampExact,
  NOT_REPORTED,
} from "@/features/devices/deviceDetailDisplay";

import { formatWeekendFlag } from "@/features/devices/remoteSettingsConfirmReconcile";
import {
  confirmedMusicScheduleFromDetail,
  formatMusicScheduleSummary,
} from "@/features/devices/musicSchedule";

import BrandButton from "@/common/BrandButton/BrandButton";
import TruncatedText from "@/common/TruncatedText/TruncatedText";
import MusicServicePaymentManage from "./MusicServiceSuspensionModal";
import { Checkbox } from "antd";
import kkFavicon from "@/assets/images/kk-favicon.png";
import kkAssistantAppLogo from "@/assets/images/kk-assistant-app-logo.png";
import globeSecondary from "@/assets/images/globe_secondary.png";
import {
  formatStructuredAddress,
  resolveAddressLine,
  StructuredAddress,
} from "@/features/devices/locationAddressFormat";
import styles from "./DeviceRow.module.scss";

export type LocationZoneSaveInput = {
  locationId: string;
  zoneId: string;
  locationName: string;
  expectedLocationName: string;
  storeNumber: string;
  expectedStoreNumber: string;
  address: {
    street: string;
    city: string;
    state: string;
    zipCode: string;
    country: string;
  };
  expectedAddress: {
    street: string;
    city: string;
    state: string;
    zipCode: string;
    country: string;
  };
  zoneName: string;
  expectedZoneName: string;
};

interface DeviceRowProps extends Device {
  checked: boolean;
  onChangeCheckbox: (event: any, device: Device) => void;
  onClickAddress: (address: string) => void;
  onSaveLocationZone?: (input: LocationZoneSaveInput) => Promise<unknown>;
}

const cell = {
  padding: 8,
  borderRight: "1px solid #e0e0e0",
} as const;

const cellLast = { padding: 8 } as const;

function healthValue(value: string | null | undefined) {
  const exact = healthTimestampExact(value);
  return (
    <span title={exact || undefined}>{formatHealthTimestamp(value)}</span>
  );
}

const structuredFromDevice = (device: Device): StructuredAddress => ({
  street: device.address_street || "",
  city: device.address_city || "",
  state: device.address_state || "",
  zipCode: device.address_zipCode || "",
  country: device.address_country || "",
});

const emptyDraft = (device: Device) => {
  const addressBaseline = structuredFromDevice(device);
  return {
    locationName: device.location_name || "",
    storeNumber: device.store_number || "",
    /** Single-line edit value; structured fields resolved on save. */
    addressLine: formatStructuredAddress(addressBaseline),
    addressBaseline,
    zoneName: device.zone_name || "",
  };
};

/** Canonical location+zone edit is only valid when both IDs are present. */
export function canEditLocationZone(device: Pick<Device, "location_id" | "zone_id">): boolean {
  return Boolean(device.location_id && device.zone_id);
}

const DeviceRow = (props: DeviceRowProps) => {
  const { checked, onChangeCheckbox, onClickAddress, onSaveLocationZone, ...device } = props;
  const [expanded, setExpanded] = useState(false);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState("");
  const [feedbackOk, setFeedbackOk] = useState(false);
  const [draft, setDraft] = useState(() => emptyDraft(device));
  const [wifiDetailsOpen, setWifiDetailsOpen] = useState(false);

  const editable = Boolean(onSaveLocationZone) && canEditLocationZone(device);

  // Device Details expand-only GET (not Remote Console). Inventory idle
  // doctrine keeps DEVICE_DETAIL_QUERY_OPTIONS.pollingInterval at 0; while
  // expanded we overlay Option B live refresh so device-originated App Lock /
  // Volume / Master / Wake-Sleep updates appear without a browser refresh.
  // Collapse → skip=true (no polling). Cache key is device.id.
  const deviceInfoLiveRefreshMs = decideDeviceInfoLiveRefreshMs({ expanded });
  const {
    data: detailResponse,
    error: detailError,
    isFetching,
    isLoading,
    isError,
    refetch,
  } = useGetDeviceDetailQuery(device.id, {
    skip: !expanded || !device.id,
    ...DEVICE_DETAIL_QUERY_OPTIONS,
    pollingInterval: deviceInfoLiveRefreshMs,
  });

  const { reportState } = useDeviceInfoOnDemandReport({
    expanded,
    deviceId: device.id,
    detailResponse,
    isLoading,
    isFetching,
    isError,
    refetch,
  });

  const {
    uiState: speedTestState,
    disabledReason: speedTestDisabled,
    runTest: runSpeedTest,
  } = useDownloadSpeedTest({
    device,
    detailResponse,
    refetch,
  });

  const detailForRow =
    detailResponse &&
    detailMatchesDevice(detailResponse.deviceId, device.id)
      ? detailResponse.detail
      : undefined;

  const handleOnClickAddress = () => onClickAddress(device.address);

  const handleOnchangeCheckbox = (event: any) =>
    onChangeCheckbox(event, device);

  const handleArrowClick = (event: React.MouseEvent) => {
    event.stopPropagation();
    setExpanded((prev) => !prev);
  };

  const beginEdit = (event: React.MouseEvent) => {
    event.stopPropagation();
    if (!editable) return;
    setDraft(emptyDraft(device));
    setFeedback("");
    setFeedbackOk(false);
    setEditing(true);
  };

  const cancelEdit = (event?: React.MouseEvent) => {
    event?.stopPropagation();
    setDraft(emptyDraft(device));
    setFeedback("");
    setFeedbackOk(false);
    setEditing(false);
  };

  const saveEdit = async (event: React.MouseEvent) => {
    event.stopPropagation();
    if (!draft.locationName.trim() || !draft.zoneName.trim() || !draft.addressLine.trim()) {
      setFeedbackOk(false);
      setFeedback("Location name, address, and zone name are required");
      return;
    }
    if (!device.location_id || !device.zone_id || !onSaveLocationZone) {
      setFeedbackOk(false);
      setFeedback("This row has no location and zone to edit");
      return;
    }
    const resolved = resolveAddressLine(draft.addressLine, draft.addressBaseline);
    if (!resolved.ok) {
      setFeedbackOk(false);
      setFeedback(resolved.error);
      return;
    }
    const address = resolved.address;
    setSaving(true);
    setFeedback("");
    try {
      await onSaveLocationZone({
        locationId: device.location_id,
        zoneId: device.zone_id,
        locationName: draft.locationName.trim(),
        expectedLocationName: device.location_name || "",
        storeNumber: draft.storeNumber.trim(),
        expectedStoreNumber: device.store_number || "",
        address,
        expectedAddress: structuredFromDevice(device),
        zoneName: draft.zoneName.trim(),
        expectedZoneName: device.zone_name || "",
      });
      setFeedbackOk(true);
      setFeedback("Saved");
      setEditing(false);
    } catch (error) {
      const status =
        error && typeof error === "object" && "status" in error
          ? (error as { status?: number }).status
          : undefined;
      const message =
        (error as { data?: { message?: string } })?.data?.message ||
        (status === 409
          ? "This location or zone was updated elsewhere. Review your changes and try again."
          : "Could not save");
      setFeedbackOk(false);
      setFeedback(message);
      // Keep editing + draft so validation/409 does not discard unsaved work.
    } finally {
      setSaving(false);
    }
  };

  const statusIcon =
    device.status === "Good"
      ? "https://staging-assets.krosskast.com/packs/media/images/check-good-1a4127f7a115ee5e73151187b4128643.png"
      : "https://staging-assets.krosskast.com/packs/media/images/check-warning-24eae3aee6eadd92cd7095cd0c5f7c4d.png";

  const showLoading = expanded && isFetching && !detailForRow;
  const showError = expanded && isError && !detailForRow;

  // Detail snapshot is authoritative. While loading, list fields may show
  // provisionally (same registry row). After detail arrives, null → Not reported
  // (never keep list stubs or fabricated samples).
  const playerVersion =
    formatReportedValue(
      detailForRow
        ? detailForRow.player?.version
        : showLoading
          ? device.kk_version
          : null
    ) || NOT_REPORTED;
  const assistantVersion =
    formatReportedValue(
      detailForRow
        ? detailForRow.assistant?.version
        : showLoading
          ? device.assistant_version
          : null
    ) || NOT_REPORTED;
  const serialNumber =
    formatReportedValue(
      detailForRow
        ? detailForRow.os?.serialNumber
        : showLoading
          ? device.serial_number
          : null
    ) || NOT_REPORTED;
  const snapshotNote =
    expanded && detailForRow
      ? formatSnapshotNote(detailResponse)
      : null;

  return (
    <>
      <tr
        style={expanded ? { borderBottom: '1px solid #f7f7f7', background: '#f7f7f7' } : {}}
        className={[expanded ? "no-hover" : "", editing ? styles.editingRow : ""]
          .filter(Boolean)
          .join(" ")}
      >
        <td style={{ width: 40, textAlign: 'center', padding: '0 8px 0 20px', overflow: 'hidden' }}>
          <div style={{ width: 24, height: 24, overflow: 'hidden', margin: '0 auto' }}>
            <Checkbox checked={checked} onChange={handleOnchangeCheckbox} style={{ margin: 0, padding: 0, width: 24, height: 24 }} />
          </div>
        </td>
        <td
          onClick={handleArrowClick}
          style={{ cursor: "pointer", userSelect: "none" }}
          tabIndex={0}
          role="button"
          aria-label={expanded ? "Collapse" : "Expand"}
          aria-expanded={expanded}
        >
          <span style={{ marginRight: 8, display: "inline-block", verticalAlign: "middle" }}>
            <span style={{ display: "inline-block", transform: expanded ? "rotate(0deg)" : "rotate(-90deg)" }}>&#9660;</span>
          </span>
          <TruncatedText
            className={styles.cellTextInline}
            text={device.master_account}
          />
        </td>
        <td>
          {editing ? (
            <input
              className={styles.inlineField}
              aria-label="Location name"
              value={draft.locationName}
              maxLength={120}
              disabled={saving}
              onChange={(event) => setDraft({ ...draft, locationName: event.target.value })}
            />
          ) : (
            <TruncatedText className={styles.cellText} text={device.location_name} />
          )}
        </td>
        <td>
          {editing ? (
            <input
              className={styles.inlineField}
              aria-label="Store number"
              value={draft.storeNumber}
              maxLength={32}
              disabled={saving}
              onChange={(event) => setDraft({ ...draft, storeNumber: event.target.value })}
            />
          ) : (
            <TruncatedText className={styles.cellText} text={device.store_number || ""} />
          )}
        </td>
        <td>
          {editing ? (
            <input
              className={`${styles.inlineField} ${styles.addressInlineField}`}
              data-testid="address-inline-editor"
              aria-label="Address"
              value={draft.addressLine}
              maxLength={240}
              disabled={saving}
              onChange={(event) =>
                setDraft({ ...draft, addressLine: event.target.value })
              }
            />
          ) : (
            <BrandButton
              className={`p-0 ${styles.addressLink}`}
              style={{ fontSize: "12px", textAlign: "left" }}
              variant="link"
              aria-label={device.address || "Address"}
              onClick={handleOnClickAddress}
            >
              <TruncatedText className={styles.cellText} text={device.address} />
            </BrandButton>
          )}
        </td>
        <td>
          {editing ? (
            <input
              className={styles.inlineField}
              aria-label="Zone name"
              value={draft.zoneName}
              maxLength={120}
              disabled={saving}
              onChange={(event) => setDraft({ ...draft, zoneName: event.target.value })}
            />
          ) : (
            <TruncatedText className={styles.cellText} text={device.zone_name} />
          )}
        </td>
        <td>
          <TruncatedText className={styles.cellText} text={device.channel} />
        </td>
        <td>
          {device.last_track_played_at ?
            new Date(device.last_track_played_at).toLocaleDateString('en-US', {
              month: '2-digit',
              day: '2-digit',
              year: '2-digit'
            })
            : "Not Played"
          }
        </td>
        <td style={{ textAlign: 'center' }}>
          <div className="d-flex align-items-center justify-content-center flex-nowrap">
            <span style={{ marginRight: "8px", minWidth: 0, maxWidth: "100%" }}>
              <TruncatedText className={styles.statusText} text={device.status} />
            </span>
            <img
              style={{ height: "15px", width: "15px", flex: "0 0 auto" }}
              alt={`Status ${device.status}`}
              src={statusIcon}
            />
          </div>
        </td>
        <td className={styles.actionCell}>
          {editing ? (
            <div className={styles.actionControls} data-testid="location-zone-edit-actions">
              <div className={styles.actionButtonRow}>
                <BrandButton
                  type="button"
                  variant="primary"
                  size="sm"
                  className={styles.actionButton}
                  disabled={saving}
                  aria-label="Save location and zone"
                  onClick={saveEdit}
                >
                  Save
                </BrandButton>
                <BrandButton
                  type="button"
                  variant="outline-secondary"
                  size="sm"
                  className={styles.actionButton}
                  disabled={saving}
                  aria-label="Cancel location and zone edit"
                  onClick={cancelEdit}
                >
                  Cancel
                </BrandButton>
              </div>
              {feedback ? (
                <div
                  className={`${styles.feedback} ${feedbackOk ? styles.feedbackOk : styles.feedbackError}`}
                  data-testid="location-zone-edit-feedback"
                >
                  {feedback}
                </div>
              ) : null}
            </div>
          ) : editable ? (
            <BrandButton
              type="button"
              variant="outline-primary"
              size="sm"
              className={styles.actionIcon}
              aria-label="Edit location and zone"
              title="Edit location and zone"
              onClick={beginEdit}
            >
              <i className="bi bi-pencil" aria-hidden="true" />
            </BrandButton>
          ) : null}
        </td>
      </tr>
      {expanded && (
        <tr style={{ borderTop: 'none', borderTopWidth: 0, borderTopStyle: 'none' }}>
          <td colSpan={10} style={{
            padding: 0,
            background: '#f7f7f7',
            backgroundColor: '#f7f7f7',
            borderTop: 'none',
            boxShadow: 'none',
            transition: 'none',
            zIndex: 2,
            position: 'relative',
          }}>
            <div style={{
              background: '#f7f7f7',
              backgroundColor: '#f7f7f7',
              position: 'relative',
              zIndex: 2,
            }}>
              {showLoading && (
                <div style={{ padding: 8, color: '#555', fontSize: 12 }} data-testid="device-detail-loading">
                  Loading device information…
                </div>
              )}
              {showError && (
                <div style={{ padding: 8, color: '#555', fontSize: 12 }} data-testid="device-detail-error">
                  {formatDeviceDetailError(detailError)}
                </div>
              )}
              {snapshotNote && (
                <div style={{ padding: 8, color: '#555', fontSize: 12 }} data-testid="device-detail-freshness">
                  {snapshotNote}
                </div>
              )}
              {reportState === "sending" && (
                <div style={{ padding: 8, color: '#555', fontSize: 12 }} data-testid="device-info-report-status">
                  Updating from device…
                </div>
              )}
              {(reportState === "skipped_unsupported" ||
                reportState === "skipped_no_evidence") && (
                <div
                  data-testid="device-info-report-skipped"
                  data-reason={reportState}
                  hidden
                />
              )}
              <table style={{
                width: '100%',
                borderCollapse: 'collapse',
                background: '#f7f7f7',
                backgroundColor: '#f7f7f7',
                zIndex: 2,
                position: 'relative',
              }}>
                <thead>
                  <tr style={{ background: "#e5e5e5" }}>
                    <th style={{ width: "25%", textAlign: "left", padding: 8, fontWeight: 600, color: '#000', background: '#cccccc', borderRight: '1px solid #e0e0e0' }}>
                      <img src={kkFavicon} alt="KrossKast" style={{ width: 20, height: 17, marginRight: 6, verticalAlign: 'middle' }} /> KrossKast
                    </th>
                    <th style={{ width: "25%", textAlign: "left", padding: 8, fontWeight: 600, color: '#000', background: '#cccccc', borderRight: '1px solid #e0e0e0' }}>
                      <img src={kkAssistantAppLogo} alt="KrossKast Assistant" style={{ width: 20, height: 17, marginRight: 6, verticalAlign: 'middle' }} /> KrossKast Assistant
                    </th>
                    <th style={{ width: "25%", textAlign: "left", padding: 8, fontWeight: 600, color: '#000', background: '#cccccc', borderRight: '1px solid #e0e0e0' }}>
                      <span role="img" aria-label="Operating System">OS</span> Operating System
                    </th>
                    <th style={{ width: "25%", textAlign: "left", padding: 8, fontWeight: 600, color: '#000', background: '#cccccc' }}>
                      <img src={globeSecondary} alt="Internet" style={{ width: 17, height: 17, marginRight: 6, verticalAlign: 'middle' }} /> Internet
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr style={{ background: 'none', backgroundColor: 'transparent' }}>
                    <td style={cell}><b>Version:</b> {playerVersion}</td>
                    <td style={cell}><b>Version:</b> {assistantVersion}</td>
                    <td style={cell}><b>OS:</b> {formatReportedValue(detailForRow?.os?.androidVersion)}</td>
                    <td style={cellLast}>
                      <b>WiFi:</b>{" "}
                      <button
                        type="button"
                        data-testid="wifi-network-name"
                        aria-expanded={wifiDetailsOpen}
                        aria-label="WiFi network details"
                        onClick={(event) => {
                          event.stopPropagation();
                          setWifiDetailsOpen((open) => !open);
                        }}
                        style={{
                          background: "none",
                          border: "none",
                          padding: 0,
                          margin: 0,
                          cursor: "pointer",
                          font: "inherit",
                          color: "inherit",
                          textDecoration: "underline",
                        }}
                      >
                        {formatReportedValue(detailForRow?.internet?.wifiSsid)}
                      </button>
                      {wifiDetailsOpen ? (
                        <div
                          data-testid="wifi-network-details"
                          style={{ marginTop: 6, fontSize: 12, color: "#555" }}
                        >
                          <div>
                            Network Name (SSID):{" "}
                            {formatReportedValue(detailForRow?.internet?.wifiSsid)}
                          </div>
                          <div>
                            Connection Status:{" "}
                            {formatWifiConnectionStatus(
                              detailForRow?.internet?.wifiConnected
                            )}
                          </div>
                          <div>
                            Local IP Address:{" "}
                            {formatReportedValue(detailForRow?.internet?.ipWifi)}
                          </div>
                          <div>
                            MAC Address:{" "}
                            {formatReportedValue(detailForRow?.os?.wifiMac)}
                          </div>
                        </div>
                      ) : null}
                    </td>
                  </tr>
                  <tr style={{ background: 'none', backgroundColor: 'transparent' }}>
                    <td style={cell}><b>Zone Username:</b> {formatReportedValue(device.zone_username)}</td>
                    <td style={cell}><b>Heartbeat:</b> {healthValue(detailForRow?.assistant?.heartbeat)}</td>
                    <td style={cell}>
                      <b>Time Zone:</b>{" "}
                      {formatReportedValue(detailForRow?.os?.timeZone)}
                      {detailForRow?.os?.timeZoneConfirmed === true
                        ? " (confirmed)"
                        : detailForRow?.os?.timeZone
                          ? " (reported)"
                          : ""}
                    </td>
                    <td style={cellLast}><b>Ethernet:</b> {formatReportedValue(detailForRow?.internet?.ethernet)}</td>
                  </tr>
                  <tr style={{ background: 'none', backgroundColor: 'transparent' }}>
                    <td style={cell}><b>Heartbeat:</b> {healthValue(detailForRow?.player?.heartbeat)}</td>
                    <td style={cell}><b>Updated:</b> {formatReportedValue(detailForRow?.assistant?.updated)}</td>
                    <td style={cell}>
                      <b>Rebooted:</b> {healthValue(detailForRow?.os?.rebooted)}
                      {detailForRow?.os?.lastRestartKind
                        ? ` (${detailForRow.os.lastRestartKind})`
                        : ""}
                      {detailForRow?.os?.rebootInFlight === true
                        ? " — reboot in progress"
                        : ""}
                    </td>
                    <td style={cellLast}><b>IP Internal:</b> {formatReportedValue(detailForRow?.internet?.ipInternal)}</td>
                  </tr>
                  <tr style={{ background: 'none', backgroundColor: 'transparent' }}>
                    <td style={cell}><b>Updated:</b> {formatReportedValue(detailForRow?.player?.updated)}</td>
                    <td style={cell}><b>Restarted:</b> {healthValue(detailForRow?.assistant?.restarted)}</td>
                    <td style={cell}><b>Lat/Long:</b> {formatReportedValue(detailForRow?.os?.latLong)}</td>
                    <td style={cellLast}><b>IP External:</b> {formatReportedValue(detailForRow?.internet?.ipExternal)}</td>
                  </tr>
                  <tr style={{ background: 'none', backgroundColor: 'transparent' }}>
                    <td style={cell}><b>Restarted:</b> {healthValue(detailForRow?.player?.restarted)}</td>
                    <td style={cell}></td>
                    <td style={cell}><b>Serial No:</b> {serialNumber}</td>
                    <td style={cellLast}>
                      <div>
                        <b>Download:</b>{" "}
                        {formatDownloadSpeedDisplay(
                          detailForRow?.internet?.downloadTest,
                          detailForRow?.internet?.download
                        )}
                        {speedTestStatusLabel(speedTestState) ? (
                          <span
                            data-testid="download-speed-test-status"
                            style={{ marginLeft: 8, fontSize: 12, color: "#555" }}
                          >
                            {speedTestStatusLabel(speedTestState)}
                          </span>
                        ) : null}
                        {isDeviceFleetCommandsEnabled() ? (
                          <button
                            type="button"
                            className={styles.speedTestIcon}
                            data-testid="test-download-speed"
                            aria-label="Test download speed"
                            aria-busy={speedTestState === "running"}
                            disabled={Boolean(speedTestDisabled)}
                            title={speedTestDisabled || "Test Download Speed"}
                            onClick={(event) => {
                              event.stopPropagation();
                              void runSpeedTest();
                            }}
                          >
                            <i className="bi bi-speedometer2" aria-hidden="true" />
                          </button>
                        ) : null}
                      </div>
                      <div data-testid="upload-speed-display">
                        <b>Upload:</b>{" "}
                        {formatUploadSpeedDisplay(detailForRow?.internet?.upload)}
                      </div>
                    </td>
                  </tr>
                  <tr style={{ background: 'none', backgroundColor: 'transparent' }}>
                    <td style={cell}>
                      <div><b>Wake Sleep:</b> {formatReportedValue(detailForRow?.player?.wakeSleep)}</div>
                      {/* T24 — device-confirmed only; null/absent = Not reported (never draft). */}
                      <div data-testid="sleep-saturday-display">
                        <b>Sleep Saturday:</b> {formatWeekendFlag(detailForRow?.player?.sleepSaturday)}
                      </div>
                      <div data-testid="sleep-sunday-display">
                        <b>Sleep Sunday:</b> {formatWeekendFlag(detailForRow?.player?.sleepSunday)}
                      </div>
                      <div data-testid="music-scheduler-display">
                        <b>Music Scheduler:</b>{" "}
                        {formatMusicScheduleSummary(
                          confirmedMusicScheduleFromDetail(detailForRow?.player)
                        )}
                      </div>
                    </td>
                    <td style={cell}></td>
                    <td style={cell}><b>Ethernet MAC:</b> {formatReportedValue(detailForRow?.os?.ethernetMac)}</td>
                    <td style={cellLast}>
                      <span data-testid="download-speed-test-meta" style={{ fontSize: 12, color: "#555" }}>
                        Endpoint: {speedTestEndpointLabel(detailForRow?.internet?.downloadTest)}
                        {lastTestedLabel(detailForRow?.internet?.downloadTest)
                          ? ` · ${lastTestedLabel(detailForRow?.internet?.downloadTest)}`
                          : ""}
                      </span>
                    </td>
                  </tr>
                  <tr style={{ background: 'none', backgroundColor: 'transparent' }}>
                    <td style={cell}><b>Volume:</b> {formatPercentDisplay(detailForRow?.player?.volume)}</td>
                    <td style={cell}></td>
                    <td style={cell}><b>Wi-Fi MAC:</b> {formatReportedValue(detailForRow?.os?.wifiMac)}</td>
                    <td style={cellLast}>
                      <span data-testid="download-speed-test-disclaimer" style={{ fontSize: 11, color: "#666" }}>
                        {SPEED_TEST_DISCLAIMER}
                      </span>
                    </td>
                  </tr>
                  <tr style={{ background: 'none', backgroundColor: 'transparent' }}>
                    <td style={cell}><b>Master Level:</b> {formatPercentDisplay(detailForRow?.player?.masterLevel)}</td>
                    <td style={cell}></td>
                    <td style={cell}></td>
                    <td style={cellLast}></td>
                  </tr>
                  <tr style={{ background: 'none', backgroundColor: 'transparent' }}>
                    <td style={cell}><b>App Lock:</b> {formatAppLockDisplay(detailForRow?.player?.appLock)}</td>
                    <td style={cell}></td>
                    <td style={cell}></td>
                    <td style={cellLast}></td>
                  </tr>
                  <tr style={{ background: 'none', backgroundColor: 'transparent' }}>
                    <td style={cell}>
                      <b>Payment:</b> {formatReportedValue(detailForRow?.player?.payment)}
                      <MusicServicePaymentManage
                        device={device}
                        onEntitlementChanged={() => refetch()}
                      />
                    </td>
                    <td style={cell}></td>
                    <td style={cell}></td>
                    <td style={cellLast}></td>
                  </tr>
                </tbody>
              </table>
            </div>
          </td>
        </tr>
      )}
    </>
  );
};

export default DeviceRow;
